// src/features/datev-weiterberechnung/services/datevKostenService.ts

/**
 * Erfassung der DATEV-Rechnung als Kostengrundlage für die Unit Economics.
 *
 * Regeln (Entscheidung 06.10.2026):
 * - Jeder Mandantenblock ist mit seiner DATEV-Kopfsumme Kosten dieses Kunden.
 * - Der Abrechnungsbonus wird im Verhältnis der Abrechnungsgrundpreise auf alle
 *   Blöcke verteilt. Das ist dieselbe Basis, auf der DATEV ihn berechnet.
 * - Beraterbezogene Leistungen inklusive eigenem Bestand (Mandant 10000) bilden
 *   den Block "DATEV allgemein". Er trägt den Bonusanteil des eigenen Bestands
 *   sowie die Rundungsdifferenz der Bonusverteilung.
 * - Mandantenblöcke ohne aktiven Kunden werden als "nicht zuordenbar" gespeichert.
 * - Jede Rechnung kann nur einmal erfasst werden.
 *
 * Gespeichert wird atomar über die Datenbankfunktion datev_kostenrechnung_speichern,
 * die doppelte Erfassung und alle Summen ein zweites Mal prüft.
 */

import { supabase } from '../../../shared/services/supabase';
import { datevWeiterberechnungService } from './datevWeiterberechnungService';
import {
  ParsedDatevInvoice,
  ParsedMandant,
  ABRECHNUNGSGRUNDPREIS_ARTIKEL,
  ABRECHNUNGSBONUS_PATTERN,
  DatevKostenblock,
  DatevKostenErfassung,
  DatevKostenModus,
  DatevKostenBereitsErfasst
} from '../types';

// Kennung, mit der die Datenbankfunktion eine doppelte Erfassung meldet
const FEHLER_BEREITS_ERFASST = 'DATEV_RECHNUNG_BEREITS_ERFASST';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Menge der Abrechnungsgrundpreise eines Blocks */
function grundpreisMengeDesBlocks(m: ParsedMandant): number {
  return m.positionen
    .filter(p => ABRECHNUNGSGRUNDPREIS_ARTIKEL.includes(p.artikelnr))
    .reduce((sum, p) => sum + p.menge, 0);
}

/** Abrechnungsbonus eines Blocks (nur im beraterbezogenen Block vorhanden) */
function bonusDesBlocks(m: ParsedMandant): number {
  if (!m.istBeraterBlock) return 0;
  return round2(
    m.positionen
      .filter(p => !p.artikelnr && ABRECHNUNGSBONUS_PATTERN.test(p.bezeichnung))
      .reduce((sum, p) => sum + p.gesamtpreis, 0)
  );
}

/** Bereinigt die Fehlermeldung der Datenbankfunktion um die technische Kennung */
function lesbareFehlermeldung(message: string): string {
  return message.replace(/^[A-Z_]+:\s*/, '');
}

export const datevKostenService = {
  /**
   * Prüft, ob eine DATEV-Rechnung bereits erfasst wurde.
   * Ohne Berater- oder Rechnungsnummer ist keine Prüfung möglich (Rückgabe null).
   */
  async pruefeBereitsErfasst(
    beraternummer: string | null,
    rechnungsnummer: string | null
  ): Promise<DatevKostenBereitsErfasst | null> {
    if (!beraternummer || !rechnungsnummer) return null;

    const { data, error } = await supabase
      .from('datev_kostenrechnungen')
      .select('created_at, modus')
      .eq('beraternummer', beraternummer)
      .eq('rechnungsnummer', rechnungsnummer)
      .maybeSingle();

    if (error) {
      console.error('[DatevKosten] Fehler bei der Prüfung auf doppelte Erfassung:', error);
      throw new Error('Die Prüfung auf doppelte Erfassung ist fehlgeschlagen: ' + error.message);
    }

    if (!data) return null;
    return { erfasstAm: data.created_at as string, modus: data.modus as DatevKostenModus };
  },

  /**
   * Berechnet die Kostenblöcke einer geparsten DATEV-Rechnung.
   * Ändert nichts in der Datenbank.
   */
  async berechne(parsed: ParsedDatevInvoice): Promise<DatevKostenErfassung> {
    if (!parsed.beraternummer || !parsed.rechnungsnummer) {
      throw new Error(
        'Beraternummer oder Rechnungsnummer konnten nicht aus der DATEV-Rechnung gelesen werden. ' +
        'Ohne beide Angaben kann die Rechnung nicht erfasst werden.'
      );
    }
    if (parsed.nettoSummeLautPdf === null) {
      throw new Error('Der Nettobetrag konnte nicht aus dem Rechnungsfuß gelesen werden.');
    }

    const beraterBloecke = parsed.mandanten.filter(m => m.istBeraterBlock);
    if (beraterBloecke.length !== 1) {
      throw new Error(
        `Die DATEV-Rechnung enthält ${beraterBloecke.length} beraterbezogene Blöcke, erwartet wird genau einer.`
      );
    }

    const summeKopf = round2(parsed.mandanten.reduce((sum, m) => sum + m.gesamtbetragLautDatev, 0));
    if (summeKopf !== round2(parsed.nettoSummeLautPdf)) {
      throw new Error(
        `Die Summe aller Blöcke (${summeKopf.toFixed(2)} EUR) weicht vom Nettobetrag ` +
        `(${parsed.nettoSummeLautPdf.toFixed(2)} EUR) ab.`
      );
    }

    const bonus = round2(parsed.mandanten.reduce((sum, m) => sum + bonusDesBlocks(m), 0));
    const grundpreisGesamt = parsed.mandanten.reduce((sum, m) => sum + grundpreisMengeDesBlocks(m), 0);
    if (bonus !== 0 && grundpreisGesamt <= 0) {
      throw new Error('Die Rechnung enthält einen Abrechnungsbonus, aber keine Abrechnungsgrundpreise.');
    }

    const customerMap = await datevWeiterberechnungService.ladeMandantenAufloesung(parsed.beraternummer);

    const bloecke: DatevKostenblock[] = parsed.mandanten.map(m => {
      const menge = grundpreisMengeDesBlocks(m);
      const kopfsumme = round2(m.gesamtbetragLautDatev);

      if (m.istBeraterBlock) {
        return {
          datevMandantennummer: m.datevMandantennummer,
          datevName: m.datevName,
          kategorie: 'allgemein',
          customerId: null,
          customerCompany: null,
          kopfsumme,
          kostenOhneBonus: round2(kopfsumme - bonusDesBlocks(m)),
          bonusAnteil: 0, // wird unten als Rest gesetzt
          grundpreisMenge: menge,
          positionen: m.positionen
        };
      }

      const kunde = customerMap.get(m.datevMandantennummer);
      const aktiv = !!kunde && kunde.deleted !== true;

      return {
        datevMandantennummer: m.datevMandantennummer,
        datevName: m.datevName,
        kategorie: aktiv ? 'mandant' : 'nicht_zuordenbar',
        customerId: aktiv && kunde ? kunde.id : null,
        customerCompany: aktiv && kunde ? kunde.company : null,
        kopfsumme,
        kostenOhneBonus: kopfsumme,
        bonusAnteil: grundpreisGesamt > 0 ? round2(bonus * menge / grundpreisGesamt) : 0,
        grundpreisMenge: menge,
        positionen: m.positionen
      };
    });

    // Der allgemeine Block trägt den Bonusanteil des eigenen Bestands und die
    // Rundungsdifferenz, damit die verteilte Summe exakt dem Bonus entspricht.
    const allgemein = bloecke.find(b => b.kategorie === 'allgemein');
    if (allgemein) {
      const verteilt = bloecke
        .filter(b => b !== allgemein)
        .reduce((sum, b) => sum + b.bonusAnteil, 0);
      allgemein.bonusAnteil = round2(bonus - verteilt);
    }

    const summeKosten = round2(bloecke.reduce((sum, b) => sum + b.kostenOhneBonus + b.bonusAnteil, 0));
    if (summeKosten !== round2(parsed.nettoSummeLautPdf)) {
      throw new Error(
        `Die verteilten Kosten (${summeKosten.toFixed(2)} EUR) weichen vom Nettobetrag ` +
        `(${parsed.nettoSummeLautPdf.toFixed(2)} EUR) ab.`
      );
    }

    return {
      beraternummer: parsed.beraternummer,
      rechnungsnummer: parsed.rechnungsnummer,
      rechnungsdatum: parsed.rechnungsdatum,
      year: parsed.year,
      month: parsed.month,
      nettoSumme: round2(parsed.nettoSummeLautPdf),
      abrechnungsbonus: bonus,
      grundpreisMenge: grundpreisGesamt,
      bloecke
    };
  },

  /**
   * Berechnet und speichert die DATEV-Rechnung in einem Schritt.
   * Bricht ab, wenn die Rechnung bereits erfasst ist oder eine Summe nicht aufgeht.
   */
  async speichern(
    parsed: ParsedDatevInvoice,
    quelldatei: string | null,
    modus: DatevKostenModus
  ): Promise<DatevKostenErfassung> {
    const erfassung = await datevKostenService.berechne(parsed);

    const { error } = await supabase.rpc('datev_kostenrechnung_speichern', {
      p_rechnung: {
        beraternummer: erfassung.beraternummer,
        rechnungsnummer: erfassung.rechnungsnummer,
        rechnungsdatum: erfassung.rechnungsdatum,
        year: erfassung.year,
        month: erfassung.month,
        netto_summe: erfassung.nettoSumme,
        abrechnungsbonus: erfassung.abrechnungsbonus,
        grundpreis_menge: erfassung.grundpreisMenge,
        quelldatei,
        modus
      },
      p_bloecke: erfassung.bloecke.map(b => ({
        datev_mandantennummer: b.datevMandantennummer,
        datev_name: b.datevName,
        kategorie: b.kategorie,
        customer_id: b.customerId,
        kopfsumme: b.kopfsumme,
        kosten_ohne_bonus: b.kostenOhneBonus,
        bonus_anteil: b.bonusAnteil,
        grundpreis_menge: b.grundpreisMenge,
        positionen: b.positionen
      }))
    });

    if (error) {
      console.error('[DatevKosten] Fehler beim Speichern:', error);
      if ((error.message || '').includes(FEHLER_BEREITS_ERFASST)) {
        throw new Error(lesbareFehlermeldung(error.message) + ' Ein erneuter Upload ist gesperrt.');
      }
      throw new Error('Die DATEV-Rechnung konnte nicht erfasst werden: ' + lesbareFehlermeldung(error.message || ''));
    }

    return erfassung;
  }
};
